/**
 * MetaExtraCredentials — tokens adicionales de Meta para la organizacion.
 *
 * POR QUE HACE FALTA. Un System User vive dentro de UN portafolio de negocio y
 * solo alcanza los activos de ese portafolio. Si la agencia que pauta sobre la
 * marca trabaja desde su propio portafolio y no se puede compartir la cuenta
 * hacia el nuestro, el token principal no la vera nunca: hace falta un segundo
 * System User, creado alla, con su propio token.
 *
 * Estas credenciales son SOLO para cuentas publicitarias. Lo organico (Pagina e
 * Instagram) sigue colgando de la credencial principal de arriba, que no se
 * toca: asi dar de alta una agencia no puede romper los tableros de Facebook e
 * Instagram, que no tienen nada que ver con esto.
 *
 * El token se envia una sola vez y se guarda cifrado; para editar solo las
 * cuentas, se deja el campo de token vacio.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  FaKey, FaPlus, FaTrash, FaSpinner, FaExclamationTriangle, FaCheck,
} from 'react-icons/fa';
import { metaApi } from '../../services/metaService';
import './MetaExtraCredentials.css';

const EMPTY = { label: '', accessToken: '', adAccountId: '' };

export default function MetaExtraCredentials({ username }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!username) return;
    try {
      const r = await metaApi.listCredentials(username);
      setList(r.credentials || []);
    } catch (e) {
      setErr(e.message || String(e));
    }
  }, [username]);

  useEffect(() => { load(); }, [load]);

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await metaApi.saveCredential(username, {
        label: form.label.trim(),
        accessToken: form.accessToken.trim(),
        adAccountId: form.adAccountId.trim(),
      });
      setList(r.credentials || []);
      setForm(EMPTY);
      setMsg('Credencial guardada. Ya aparece en el selector de Campañas (API).');
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (label) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await metaApi.deleteCredential(username, label);
      setList(r.credentials || []);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const canSave = form.label.trim() && form.adAccountId.trim() && !busy;

  return (
    <section className="mxc-card">
      <h3 className="mxc-title"><FaKey /> Credenciales adicionales</h3>
      <p className="mxc-hint">
        Un System User solo alcanza las cuentas de <strong>su</strong> portafolio de negocio.
        Si una agencia externa pauta desde otro portafolio y no se puede compartir la cuenta
        hacia el nuestro, da de alta aquí su token. Estas credenciales son solo para cuentas
        publicitarias: la Página y el Instagram siguen usando la credencial principal.
      </p>

      {!!list.length && (
        <div className="mxc-list">
          {list.map((c) => (
            <div className="mxc-item" key={c.label}>
              <div className="mxc-item-main">
                <strong>{c.label}</strong>
                <span className="mxc-item-accounts">
                  {(c.ad_account_ids || []).join(' · ') || 'sin cuentas'}
                </span>
              </div>
              <span className={`mxc-tag ${c.has_token ? 'ok' : 'bad'}`}>
                {c.has_token ? 'con token' : 'sin token'}
              </span>
              <button type="button" className="mxc-btn" disabled={busy}
                onClick={() => remove(c.label)} title="Eliminar esta credencial">
                <FaTrash />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mxc-grid">
        <label className="mxc-field">
          <span className="mxc-label">Etiqueta</span>
          <input className="mxc-input" value={form.label} onChange={onField('label')}
            placeholder="Havas" />
          <span className="mxc-hint">Con qué nombre la verás en el selector.</span>
        </label>
        <label className="mxc-field">
          <span className="mxc-label">Ad Account ID</span>
          <input className="mxc-input mono" value={form.adAccountId} onChange={onField('adAccountId')}
            placeholder="act_2198778420876594" />
          <span className="mxc-hint">Varias separadas por coma, si aplica.</span>
        </label>
      </div>
      <label className="mxc-field">
        <span className="mxc-label">System User Access Token</span>
        <input className="mxc-input mono" type="password" value={form.accessToken}
          onChange={onField('accessToken')} placeholder="EAAG..." autoComplete="off" />
        <span className="mxc-hint">
          Se guarda cifrado y nunca se muestra de vuelta. Déjalo vacío si solo quieres
          editar las cuentas de una credencial que ya existe.
        </span>
      </label>

      <div className="mxc-actions">
        <button type="button" className="mxc-btn primary" disabled={!canSave} onClick={save}>
          {busy ? <FaSpinner className="mxc-spin" /> : <FaPlus />} Guardar credencial
        </button>
      </div>

      {msg && <p className="mxc-notice ok"><FaCheck /> {msg}</p>}
      {err && <p className="mxc-notice bad"><FaExclamationTriangle /> {err}</p>}
    </section>
  );
}
